// src/hooks/useFeedbackApi.ts
//
// 피드백 게시판 API 훅. useContainerRentalApi.ts 와 같은 골격이다 —
// useApi().apiFetch 로 토큰을 얹고, env 기반 base URL, flat async/await.
//
// 두 가지만 기존 훅과 다르다:
//  1) 에러 본문 파싱(readError)을 **전 호출**에 적용한다. 서버 계약이
//     `{"error": "..."}` 단일 키로 확정되어 있어 안전하다.
//  2) 작성 계열 요청 — 글·댓글 작성/수정 **그리고 상태 변경(PATCH)** — 에
//     `authorName`(표시명)을 자동으로 실어 보낸다. 액세스 토큰에 name 클레임이
//     없어 서버가 표시명을 얻을 수단이 없기 때문이다(백엔드 설계서 §3).
//     상태 변경도 예외가 아니다: 서버가 status_changed_by_name 을 Select 화이트
//     리스트로 강제 기록하므로, 안 보내면 빈 문자열이 덮어써진다.
//     호출부가 authorName 을 명시하면 그 값이 우선한다(라운드트립 안전).
//     인가는 언제나 서버가 JWT sub 로 한다 — 이 값은 순수 표시용 스냅샷이다.
//     TODO(feedback): Auth0 Action 으로 액세스 토큰에 커스텀 클레임
//     ("https://sales.ourmars.net/name")을 주입하고 서버가
//     validator.WithCustomClaims 를 연결하면 이 payload 필드는 폴백으로만
//     남는다. 그때도 프론트 계약은 바뀌지 않는다.

import { useAuth0 } from "@auth0/auth0-react";

import { useApi } from "./useApi.ts";
import type {
  FeedbackCommentDto,
  FeedbackCommentInput,
  FeedbackListParams,
  FeedbackListResponse,
  FeedbackPostDto,
  FeedbackPostInput,
  FeedbackStatus,
} from "../contracts/feedback/types";

const API_BASE = process.env.REACT_APP_API_BASE as string;

/**
 * 서버 에러 본문에서 사용자에게 보여줄 메시지를 뽑는다.
 *
 * 401 만 예외다 — internal/auth/middleware.go 의 WithErrorHandler 가
 * `http.Error(w, err.Error(), 401)` 로 **plain text** 를 내려주기 때문에
 * res.json() 이 던진다. 전 도메인 공통 이슈이며 이번 범위에서 서버를 고치지
 * 않으므로 여기서 분기한다.
 */
export async function readError(
  res: Response,
  fallback: string
): Promise<string> {
  if (res.status === 401) {
    return "로그인이 만료되었습니다. 페이지를 새로고침해 주세요.";
  }
  try {
    const body = await res.json();
    if (body && typeof body.error === "string" && body.error.trim()) {
      return body.error as string;
    }
  } catch {
    /* JSON 이 아닌 본문 — 폴백으로 넘어간다 */
  }
  return `${fallback} (${res.status})`;
}

/** HTTP 상태를 들고 다니는 에러. 호출부가 404/403 을 문구가 아니라 코드로 분기한다. */
export interface FeedbackApiError extends Error {
  status: number;
}

/**
 * 실패 응답을 예외로 바꾼다.
 *
 * 메시지 문자열만 던지면 호출부가 404 를 "not found" 같은 문구 매칭으로
 * 알아내야 하는데, 서버 문구가 바뀌는 순간 조용히 깨진다. status 를 함께
 * 실어 보내 분기를 코드에 붙인다.
 */
async function throwApiError(res: Response, fallback: string): Promise<never> {
  const err = new Error(await readError(res, fallback)) as FeedbackApiError;
  err.status = res.status;
  throw err;
}

export const useFeedbackApi = () => {
  const { apiFetch } = useApi();
  const { user } = useAuth0();

  // DashboardLayout.tsx 의 displayName 식을 그대로 재사용한다(백엔드 설계서 §3-2).
  const displayName = (user && (user.name || user.nickname || user.email)) || "";

  /* ---------- 글 ---------- */

  const listPosts = async (
    params: FeedbackListParams
  ): Promise<FeedbackListResponse> => {
    const qs = new URLSearchParams();
    // 빈 문자열은 붙이지 않는다 — 서버가 ""를 필터로 오해하지 않게.
    if (params.q) qs.append("q", params.q);
    if (params.category) qs.append("category", params.category);
    if (params.status) qs.append("status", params.status);
    if (params.page !== undefined) qs.append("page", String(params.page));
    if (params.size !== undefined) qs.append("size", String(params.size));
    if (params.sort) qs.append("sort", params.sort);

    const suffix = qs.toString();
    const url = suffix ? `${API_BASE}/feedback?${suffix}` : `${API_BASE}/feedback`;

    const res = await apiFetch(url);
    if (!res.ok) await throwApiError(res, "목록을 불러오지 못했습니다.");
    return (await res.json()) as FeedbackListResponse;
  };

  const getPost = async (id: string): Promise<FeedbackPostDto> => {
    const res = await apiFetch(`${API_BASE}/feedback/${id}`);
    if (!res.ok) await throwApiError(res, "글을 불러오지 못했습니다.");
    return (await res.json()) as FeedbackPostDto;
  };

  const createPost = async (
    input: FeedbackPostInput
  ): Promise<FeedbackPostDto> => {
    const res = await apiFetch(`${API_BASE}/feedback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...input, authorName: input.authorName?.trim() || displayName }),
    });
    if (!res.ok) await throwApiError(res, "등록하지 못했습니다.");
    return (await res.json()) as FeedbackPostDto;
  };

  const updatePost = async (
    id: string,
    input: FeedbackPostInput
  ): Promise<FeedbackPostDto> => {
    const res = await apiFetch(`${API_BASE}/feedback/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...input, authorName: input.authorName?.trim() || displayName }),
    });
    if (!res.ok) await throwApiError(res, "수정하지 못했습니다.");
    return (await res.json()) as FeedbackPostDto;
  };

  const deletePost = async (id: string): Promise<void> => {
    const res = await apiFetch(`${API_BASE}/feedback/${id}`, {
      method: "DELETE",
    });
    if (!res.ok) await throwApiError(res, "삭제하지 못했습니다.");
  };

  /**
   * 상태 변경 — 작성자가 아니어도 누구나 호출할 수 있다(총괄 확정).
   * 응답은 갱신된 **글 전체**(댓글 제외)이므로 목록/상세에서 재조회 없이
   * in-place 교체하면 된다.
   *
   * authorName 을 함께 보낸다 — 서버가 "누가 상태를 바꿨는지"를 이 값으로만
   * 알 수 있고, 빼면 statusChangedByName 이 영구히 빈 문자열이 된다.
   */
  const changeStatus = async (
    id: string,
    status: FeedbackStatus
  ): Promise<FeedbackPostDto> => {
    const res = await apiFetch(`${API_BASE}/feedback/${id}/status`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status, authorName: displayName }),
    });
    if (!res.ok) await throwApiError(res, "상태를 변경하지 못했습니다.");
    return (await res.json()) as FeedbackPostDto;
  };

  /* ---------- 댓글 ---------- */

  const createComment = async (
    postId: string,
    input: FeedbackCommentInput
  ): Promise<FeedbackCommentDto> => {
    const res = await apiFetch(`${API_BASE}/feedback/${postId}/comments`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...input, authorName: input.authorName?.trim() || displayName }),
    });
    if (!res.ok) await throwApiError(res, "댓글을 등록하지 못했습니다.");
    return (await res.json()) as FeedbackCommentDto;
  };

  const updateComment = async (
    postId: string,
    commentId: string,
    input: FeedbackCommentInput
  ): Promise<FeedbackCommentDto> => {
    const res = await apiFetch(
      `${API_BASE}/feedback/${postId}/comments/${commentId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...input, authorName: input.authorName?.trim() || displayName }),
      }
    );
    if (!res.ok) await throwApiError(res, "댓글을 수정하지 못했습니다.");
    return (await res.json()) as FeedbackCommentDto;
  };

  const deleteComment = async (
    postId: string,
    commentId: string
  ): Promise<void> => {
    const res = await apiFetch(
      `${API_BASE}/feedback/${postId}/comments/${commentId}`,
      { method: "DELETE" }
    );
    if (!res.ok) await throwApiError(res, "댓글을 삭제하지 못했습니다.");
  };

  return {
    listPosts,
    getPost,
    createPost,
    updatePost,
    deletePost,
    changeStatus,
    createComment,
    updateComment,
    deleteComment,
  };
};

export default useFeedbackApi;
